<template>
  <div id="dt-viewer-app" class="decision-tree-viewer-app" :class="classModifiers">
    <View v-if="loading" class="dt-viewer-app__view-app-loading" :preview-mode="previewMode">
      <sp-progress
        type="ellipsis"
        size="xx-small"
        color="var(--sp-comp-dtv-animated-ellipsis-color, var(--sp-sys-color-primary))"
      ></sp-progress>
    </View>
    <DecisionTreeViewerPreviewView v-else-if="previewMode" />
    <DecisionTreeViewerView v-else-if="decisionTree" />
  </div>
</template>

<script setup>
import { useMediaQuery } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { computed, onMounted, ref } from "vue";

import View from "./components/View.vue";
import DecisionTreeViewerPreviewView from "./views/DecisionTreeViewerPreviewView.vue";
import DecisionTreeViewerView from "./views/DecisionTreeViewerView.vue";

import { useDecisionTreeStore } from "./store/decision-tree";
import { useStateStore } from "./store/state";
import { useVisitTypeStore } from "./store/visit-type";

const { load: loadStates } = useStateStore();
const { load: loadVisitTypes } = useVisitTypeStore();
const decisionTreeStore = useDecisionTreeStore();
const { decisionTree, previewMode } = storeToRefs(decisionTreeStore);
const { load: loadDecisionTree, start: startDecisionTree } = decisionTreeStore;

const loading = ref(true);

const xsBreakpoint = computed(() =>
  window.getComputedStyle(document.documentElement).getPropertyValue("--sp-ref-breakpoint-xs"),
);

const isLteXsScreen = useMediaQuery(`(max-width: ${xsBreakpoint.value})`);
const classModifiers = computed(() => ({
  "--bp-lte-xs-screen": isLteXsScreen.value,
}));

onMounted(async () => {
  loading.value = true;

  try {
    await Promise.all([loadStates(), loadVisitTypes(), loadDecisionTree()]);

    // Start before flipping `loading` off so the viewer only ever mounts with
    // currentNode already set — no pre-start null window. The viewer dispatches
    // the first node's progress from its onMounted, so Ahoy still records it.
    if (!previewMode.value && decisionTree.value) {
      startDecisionTree();
    }
  } catch (err) {
    // Decision tree config can be missing (e.g. server returns 404 for a
    // referenced tree id). Without this catch, Promise.all's rejection
    // bubbles past `loading=false` and the viewer mounts against a null
    // decisionTree, throwing in DecisionTreeViewerView's currentNode
    // computed. Surface the failure but keep the host app running.
    console.error("[DecisionTreeViewer] failed to load:", err);
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped lang="scss">
.decision-tree-viewer-app {
  --dtv-font-size: var(--sp-sys-body-font-size, 1rem);
  --dtv-app-content-min-height: var(
    --sp-comp-dtv-content-min-height,
    clamp(20rem, 42vh, 31.25rem)
  ); // is needed for View and Card components because of the slide transition/animation
  --dtv-app-margin-bottom: var(--sp-comp-dtv-app-margin-bottom, 4rem);
  --dtv-app-margin-bottom-mobile: var(--sp-comp-dtv-app-margin-bottom-mobile, var(--dtv-app-margin-bottom));

  font-size: var(--dtv-font-size);
  margin-bottom: var(--dtv-app-margin-bottom);

  // mobile last :-( - TODO @mgruschetzki: refactor to mobile first
  &.--bp-lte-xs-screen {
    --dtv-app-margin-bottom: var(--dtv-app-margin-bottom-mobile);

    // <View>
    --dtv-view-padding-block: var(--sp-comp-dtv-view-padding-block-mobile, 0.5rem);
    --dtv-view-fade-out-pseudo-slides-display: var(--sp-comp-dtv-view-fade-out-pseudo-slides-display-mobile, none);

    // <Card>
    --dtv-card-actions-grid-columns: var(--sp-comp-dtv-card-actions-grid-columns-mobile, 1fr);
    --dtv-card-actions-grid-areas: var(
      --sp-comp-dtv-card-actions-grid-areas-mobile,
      "end-action" "center-action" "start-action"
    );
    --dtv-card-border: var(--sp-comp-dtv-card-border-mobile, none);
    --dtv-card-box-shadow: var(--sp-comp-dtv-card-box-shadow-mobile, none);
    --dtv-card-padding-block: var(--sp-comp-dtv-card-padding-block-mobile, 0);
    --dtv-card-padding-inline: var(--sp-comp-dtv-card-padding-inline-mobile, 0);

    // <Slide>
    --dtv-slide-nav-btn-margin-top: 1rem;
    --dtv-slide-nav-btn-min-width: var(--sp-comp-dtv-slide-nav-btn-min-width-mobile, 100%);

    // <Node>
    --dtv-node-title-font: var(--sp-comp-dtv-node-title-font-mobile, var(--sp-sys-typescale-h3-short));

    // <select>
    --sp-comp-dtv-select-min-height: var(--sp-sys-form-field-compact-min-height);
  }
}

.dt-viewer-app__view-app-loading {
  align-items: center;
  margin-inline: auto;
}
</style>
